import { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';

export default function MentionInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  users = [],
  className = ''
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursor = e.target.selectionStart;
    setCursorPosition(cursor);
    onChange(e);

    // Check for @ mention
    const textBeforeCursor = newValue.slice(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      // Only show suggestions if there's no space after @
      if (!textAfterAt.includes(' ')) {
        setMentionStart(lastAtIndex);
        const searchTerm = textAfterAt.toLowerCase();
        const filtered = users.filter(user =>
          user.name.toLowerCase().includes(searchTerm) ||
          user.email.toLowerCase().includes(searchTerm)
        ).slice(0, 5);
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectUser = (user) => {
    if (mentionStart === -1) return;

    const beforeMention = value.slice(0, mentionStart);
    const afterCursor = value.slice(cursorPosition);
    const newValue = `${beforeMention}@${user.name} ${afterCursor}`;

    onChange({ target: { value: newValue } });
    setShowSuggestions(false);
    setMentionStart(-1);

    // Focus back to input
    setTimeout(() => {
      if (inputRef.current) {
        const newCursor = beforeMention.length + user.name.length + 2;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };

  const handleKeyDownInternal = (e) => {
    if (showSuggestions) {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
      if (e.key === 'Tab' && suggestions.length > 0) {
        e.preventDefault();
        handleSelectUser(suggestions[0]);
        return;
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div className="relative flex-1">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDownInternal}
        placeholder={placeholder}
        className={className}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 max-h-48 overflow-y-auto"
        >
          <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            Kullanıcı etiketle
          </div>
          {suggestions.map((user) => (
            <button
              key={user.id}
              onClick={() => handleSelectUser(user)}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-medium">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-medium dark:text-white">{user.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper to render text with mentions highlighted
export function renderMentions(text, users = []) {
  if (!text) return text;

  // Match @username patterns
  const mentionRegex = /@(\w+(?:\s\w+)*)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const mentionName = match[1];
    const mentionedUser = users.find(u =>
      u.name.toLowerCase() === mentionName.toLowerCase()
    );

    if (mentionedUser) {
      parts.push(
        <span
          key={match.index}
          className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1 rounded font-medium"
        >
          @{mentionName}
        </span>
      );
    } else {
      parts.push(`@${mentionName}`);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}
